import { useState, useEffect, useContext } from 'react';
import api from './api/axiosInstance';
import { UserContext } from './UserContext';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Sidebar from './Sidebar.js';
import { Form, Row, Col, Card, Table, Image, Button} from 'react-bootstrap';
import CityFilter from './CityFilter';
import Pagination from 'react-bootstrap/Pagination';
import Breadcrumb from './Breadcrumb';
import { isAdminType} from './utils/roleUtils';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import icNoCities from './assets/img/ic-empty-box.svg';

const CityList = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    useEffect(() => {
        const fetchTickets = async () => {
            try {
                const res = await api.get(`/api/tickets`); // ✅ token comes from interceptor
                setTickets(res.data);
            } catch (error) {
                console.error('Error fetching tickets:', error);
            }
        };
        fetchTickets();
    }, []);
    const [cities, setCities] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    
    const [citiesPerPage, setCitiesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);
    const [sortOrderId, setSortOrderId] = useState('asc');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderCountry, setSortOrderCountry] = useState('asc');
    const [sortOrderComments, setSortOrderComments] = useState('asc');
    const [sortOrderStatus, setSortOrderStatus] = useState('asc');
    const { user } = useContext(UserContext);
    const [selectedCity] = useState('');

    useEffect(() => {
        if (user && isAdminType(user.role)) {
            fetchCities();
        }
    }, [user]);

    const fetchCities = async () => {
        try {
            const res = await api.get(`/api/cities`); // ✅ token via interceptor
            const sorted = [...res.data].sort((a, b) => {
                const A = String(a?.country?.name ?? '').trim();
                const B = String(b?.country?.name ?? '').trim();
                return A.localeCompare(B, undefined, { sensitivity: 'base', numeric: true });
            });
            setCities(sorted);
            setFilteredCities(sorted);
        } catch (error) {
            console.error('Error fetching cities:', error);
        }
    };

    // Handle search filter
    useEffect(() => {
        const lowercasedQuery = searchQuery.toLowerCase();

        const filteredResults = cities.filter((city) => {
            // ✅ safely normalize name to string
            const name = String(city?.name ?? '').toLowerCase();

            return (
                name.includes(lowercasedQuery) &&
                (selectedCity === '' || city.id === selectedCity)
            );
        });

        setFilteredCities(filteredResults);
        setCurrentPage(1); // ✅ Reset to first page when filtering
    }, [searchQuery, selectedCity, cities]);
    
    // Toggle Activity status
    const toggleActivityStatus = async (cityId, currentStatus) => {
        try {
            // Send a patch request to toggle the status
            const response = await api.patch(
                `/api/cities/toggle-status/${cityId}`,
                {}
            );
            
            const updatedStatus = response.data.activityStatus;
            console.log(`City ID: ${cityId}, Updated Status: ${updatedStatus}`);
    
            // Update state to reflect the change
            setCities(prev =>
                prev.map(city =>
                    city._id === cityId ? { ...city, activityStatus: updatedStatus } : city
                )
            );
            
            setFilteredCities(prev =>
                prev.map(city =>
                    city._id === cityId ? { ...city, activityStatus: updatedStatus } : city
                )
            );
        } catch (error) {
            console.error('Error toggling activity status:', error);
        }
    };
    

    // Sorting function for City ID
    const sortCitiesById = (direction) => {
        const next = direction ?? (sortOrderId === 'asc' ? 'desc' : 'asc');
        setSortOrderId(next);

        setFilteredCities(prev => {
            const normalize = (v) => {
            if (v === null || v === undefined) return { num: null, raw: '' };
            const raw = String(v).trim();
            // join all digit runs to be more permissive: "A-12/B34" -> "1234"
            const digits = raw.match(/\d+/g)?.join('') ?? '';
            const num = digits ? parseInt(digits, 10) : null;
            return { num, raw };
            };

            const arr = [...prev];
            arr.sort((a, b) => {
            const A = normalize(a?.cityId);
            const B = normalize(b?.cityId);

            // If both have no numeric part, fall back to lexicographic (locale-aware)
            if (A.num === null && B.num === null) {
                const cmp = A.raw.localeCompare(B.raw, undefined, { sensitivity: 'base', numeric: true });
                return next === 'asc' ? cmp : -cmp;
            }

            // Put non-numeric last in asc / first in desc
            if (A.num === null) return next === 'asc' ? 1 : -1;
            if (B.num === null) return next === 'asc' ? -1 : 1;

            // Primary numeric compare
            if (A.num !== B.num) return next === 'asc' ? A.num - B.num : B.num - A.num;

            // ✅ Stable tie-breaker by raw text
            const tie = A.raw.localeCompare(B.raw, undefined, { sensitivity: 'base', numeric: true });
            return next === 'asc' ? tie : -tie;
            });

            return arr;
        });
    };

    // Sorting function for City name
    const sortCitiesByName = () => {
        const next = sortOrderName === 'asc' ? 'desc' : 'asc';
        setSortOrderName(next);

        setFilteredCities(prev => {
            const arr = [...prev];
            // Case/diacritic-insensitive, locale-aware compare with numeric awareness
            arr.sort((a, b) => {
                const A = String(a?.name ?? '').trim();
                const B = String(b?.name ?? '').trim();
                const cmp = A.localeCompare(B, undefined, { sensitivity: 'base', numeric: true });
                return next === 'asc' ? cmp : -cmp;
            });
            return arr;
        });
    };

    // Sorting function for Country name
    const sortCitiesByCountry = () => {
        const next = sortOrderCountry === 'asc' ? 'desc' : 'asc';
        setSortOrderCountry(next);

        setFilteredCities(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
            const A = String(a?.country?.name ?? '').trim();
            const B = String(b?.country?.name ?? '').trim();
            const cmp = A.localeCompare(B, undefined, { sensitivity: 'base', numeric: true });
            return next === 'asc' ? cmp : -cmp;
            });
            return arr;
        });
    };

    // Sorting function for Comments count
    const sortCitiesByComments = () => {
        const next = sortOrderComments === 'asc' ? 'desc' : 'asc';
        setSortOrderComments(next); // ✅

        setFilteredCities(prev => {
            const countOf = (c) => {
            // ✅ supports commentsCount (number) or comments (array); falls back to 0
            const n = c?.commentsCount ?? (Array.isArray(c?.comments) ? c.comments.length : 0);
            return Number.isFinite(n) ? n : 0;
            };

            const arr = [...prev];
            arr.sort((a, b) => {
            const A = countOf(a);
            const B = countOf(b);
            if (A !== B) return next === 'asc' ? A - B : B - A;

            // ✅ stable tie-breaker: by name (case/diacritic insensitive)
            const an = String(a?.name ?? '');
            const bn = String(b?.name ?? '');
            const tie = an.localeCompare(bn, undefined, { sensitivity: 'base', numeric: true });
            return next === 'asc' ? tie : -tie;
            });
            return arr;
        });
    };



    // Sorting function for activity status
    const sortCitiesByStatus = () => {
        const next = sortOrderStatus === 'asc' ? 'desc' : 'asc';
        setSortOrderStatus(next);

        setFilteredCities(prev => {
            const arr = [...prev];
            arr.sort((a, b) => {
            const A = a?.activityStatus ? 1 : 0;
            const B = b?.activityStatus ? 1 : 0;
            // true(1) after false(0) in asc; reversed in desc
            return next === 'asc' ? A - B : B - A;
            });
            return arr;
        });
    };
    
    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const totalPages = Math.ceil(filteredCities.length / citiesPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const noCities = filteredCities.length === 0;

    return (
        <>
            <Sidebar ticketCount={tickets.length} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col xs={6}>
                                            <h1>Cities</h1>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12}>
                                            <CityFilter
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                cities={cities}
                                            />
                                        </Col>
                                    </Row>
                                    {noCities ? (
                                        <Row>
                                            <Col md={12}>
                                                <Row className="mb-3">
                                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                        <Image src={icNoCities} alt="No cities" className="mb-4" />
                                                        <h2 className="mb-3">No cities yet.</h2>
                                                        <p className="mb-4">Your cities will be displayed here.</p>
                                                        <Button as={Link}  to="/city-add" variant="primary">Add city</Button>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table>
                                                        <thead>
                                                            <tr>
                                                                <th className="th-fixed-city-id" onClick={() => sortCitiesById()} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        ID
                                                                        <span>
                                                                        <FaCaretUp   className={sortOrderId === 'asc'  ? 'text-primary' : ''} />
                                                                        <FaCaretDown className={sortOrderId === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>

                                                                <th className="th-fixed-city-name" onClick={sortCitiesByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Name
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderName === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderName === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="th-fixed-city-country" onClick={sortCitiesByCountry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Country
                                                                        <span>
                                                                            <FaCaretUp   className={sortOrderCountry === 'asc'  ? 'text-primary' : ''} />
                                                                            <FaCaretDown className={sortOrderCountry === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="th-fixed-city-comments" onClick={sortCitiesByComments} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Comments
                                                                        <span>
                                                                        <FaCaretUp   className={sortOrderComments === 'asc'  ? 'text-primary' : ''} />
                                                                        <FaCaretDown className={sortOrderComments === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th className="th-fixed-city-status" onClick={sortCitiesByStatus} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Active
                                                                        <span>
                                                                        <FaCaretUp   className={sortOrderStatus === 'asc'  ? 'text-primary' : ''} />
                                                                        <FaCaretDown className={sortOrderStatus === 'desc' ? 'text-primary' : ''} />
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCities.map((city) => (
                                                                <tr
                                                                    className="company-row discovery-row"
                                                                    key={city._id}
                                                                    onClick={() => navigate(`/city-edit/${city._id}`)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <td>{city.cityId}</td>
                                                                    <td>{city.name}</td>
                                                                    <td>{city.country?.name || '-'}</td>
                                                                    <td>{city.comments}</td>
                                                                    <td onClick={(e) => e.stopPropagation()}>
                                                                        <div className="form-check form-switch">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                role="switch"
                                                                                id={`status-switch-${city._id}`}
                                                                                checked={city.activityStatus}
                                                                                onChange={() => toggleActivityStatus(city._id, city.activityStatus)}
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                                        <Link to={`/city-edit/${city._id}`} className="btn btn-sm btn-outline-secondary">
                                                                            Edit
                                                                        </Link>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                    {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>Total&nbsp;{filteredCities.length}&nbsp;Cities</span>
                                            <Pagination>
                                                <Pagination.Prev 
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                                    disabled={currentPage === 1}
                                                />
                                                {[...Array(totalPages).keys()].map(number => (
                                                    <Pagination.Item 
                                                        key={number + 1} 
                                                        active={number + 1 === currentPage} 
                                                        onClick={() => handlePageChange(number + 1)}
                                                    >
                                                        {number + 1}
                                                    </Pagination.Item>
                                                ))}
                                                <Pagination.Next 
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>
                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={citiesPerPage}
                                                    onChange={(e) => setCitiesPerPage(Number(e.target.value))} // ✅ Updates statee
                                                    style={{ width: 'auto', marginRight: '10px' }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>
                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>
                                            <Form.Control
                                                type="number"
                                                className="pagination-controls-goto-input"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage); // ✅ Only valid pages
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                                style={{ textAlign: 'center' }}
                                            />
                                            
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Link to="/city-add" className="btn btn-outline-primary">
                                    Add New City
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default CityList;
